import { Building2, Loader2, SendHorizonal } from 'lucide-react'
import { useState } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useGenerateInvitationLink } from 'src/api/server/useGenerateInvitationLink'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Input } from 'src/shared/components/ui/input'
import { Textarea } from 'src/shared/components/ui/textarea'
import { cn } from 'src/shared/components/ui/utils'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'

type InviteMode = 'new' | 'existing'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseEmails = (value: string) =>
  value
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)

export const InviteCustomerDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { createOrganization, useSearchOrganizations } = useOrgsApi({})
  const generateInvitationLink = useGenerateInvitationLink()

  const [mode, setMode] = useState<InviteMode>('new')
  const [companyName, setCompanyName] = useState('')
  const [emails, setEmails] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [selectedOrg, setSelectedOrg] = useState<{
    id: string
    name: string
  } | null>(null)

  const debouncedOrgSearch = useDebounce(orgSearch, 300)
  const { data: orgSearchResult, isFetching: isSearchingOrgs } =
    useSearchOrganizations({
      search: debouncedOrgSearch,
      excludeAnon: true,
      excludeEmptyName: true,
      limit: 8,
      enabled:
        open &&
        mode === 'existing' &&
        !selectedOrg &&
        debouncedOrgSearch.trim().length > 0,
    })

  const orgResults = orgSearchResult?.data ?? []
  const parsedEmails = parseEmails(emails)
  const emailsAreValid =
    parsedEmails.length > 0 && parsedEmails.every((email) => EMAIL_RE.test(email))
  const emailHint =
    emails.trim() && !emailsAreValid
      ? t(
          'Enter valid email addresses, separated by commas.',
          'Ange giltiga e-postadresser, separerade med kommatecken.'
        )
      : t(
          'Separate multiple emails with commas.',
          'Separera flera e-postadresser med kommatecken.'
        )
  const isPending =
    createOrganization.isPending || generateInvitationLink.isPending
  const canSend =
    emailsAreValid &&
    !isPending &&
    (mode === 'new' ? companyName.trim().length > 0 : !!selectedOrg)

  const reset = () => {
    setMode('new')
    setCompanyName('')
    setEmails('')
    setOrgSearch('')
    setSelectedOrg(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) reset()
  }

  const handleSend = async () => {
    if (!canSend) return

    try {
      let organizationId = selectedOrg?.id

      if (mode === 'new') {
        const result = await createOrganization.mutateAsync({
          name: companyName.trim(),
          source: 'internal',
        })
        organizationId = result?.data?.[0]?.id
        if (!organizationId) {
          throw new Error(
            t('Could not create organization', 'Kunde inte skapa organisationen')
          )
        }
      }

      if (!organizationId) return

      await generateInvitationLink.mutateAsync({
        emails: parsedEmails.join(','),
        organization_id: organizationId,
      })

      handleOpenChange(false)
    } catch (error) {
      if (generateInvitationLink.isError) return
      toast({
        title: t('Something went wrong', 'Något gick fel'),
        description:
          error instanceof Error
            ? error.message
            : t('Could not send invite', 'Kunde inte skicka inbjudan'),
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('Invite customer', 'Bjud in kund')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Create a new organization or add people to one that already exists. They will get an email to join.',
              'Skapa en ny organisation eller bjud in till en som redan finns. De får ett mejl för att gå med.'
            )}
          </DialogDescription>
        </DialogHeader>

        <Row
          className={cn(
            'w-full gap-1.5 rounded-[var(--radius-outer)] border p-1.5',
            'border-[hsl(var(--border-subtle))] bg-[hsl(var(--surface-panel)/0.88)]'
          )}
        >
          <button
            type="button"
            onClick={() => setMode('new')}
            className={cn(
              'flex-1 rounded-[var(--radius-tab)] px-3 py-2 text-sm font-medium transition-all duration-200',
              mode === 'new'
                ? 'button-secondary text-foreground'
                : 'text-muted-foreground hover:bg-black/[0.045] hover:text-foreground/80'
            )}
          >
            {t('New customer', 'Ny kund')}
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={cn(
              'flex-1 rounded-[var(--radius-tab)] px-3 py-2 text-sm font-medium transition-all duration-200',
              mode === 'existing'
                ? 'button-secondary text-foreground'
                : 'text-muted-foreground hover:bg-black/[0.045] hover:text-foreground/80'
            )}
          >
            {t('Existing organization', 'Befintlig organisation')}
          </button>
        </Row>

        {mode === 'new' ? (
          <Input
            label={t('Company name', 'Företagsnamn')}
            placeholder={t('Acme AB', 'Acme AB')}
            value={companyName}
            onChange={(event) => setCompanyName(event.target.value)}
          />
        ) : (
          <Column className="gap-2">
            {selectedOrg ? (
              <Row className="items-center justify-between rounded-lg border px-3 py-2">
                <Row className="items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedOrg.name}</span>
                </Row>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedOrg(null)}
                >
                  {t('Change', 'Byt')}
                </Button>
              </Row>
            ) : (
              <>
                <Input
                  label={t('Organization', 'Organisation')}
                  placeholder={t('Search organizations…', 'Sök organisationer…')}
                  value={orgSearch}
                  onChange={(event) => setOrgSearch(event.target.value)}
                  startContent={
                    isSearchingOrgs ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    )
                  }
                />
                <Column className="max-h-48 overflow-y-auto rounded-lg border">
                  {isSearchingOrgs && orgResults.length === 0 ? (
                    <p className="px-3 py-4 text-muted-foreground">
                      {t('Searching…', 'Söker…')}
                    </p>
                  ) : orgResults.length === 0 ? (
                    <p className="px-3 py-4 text-muted-foreground">
                      {debouncedOrgSearch.trim()
                        ? t(
                            'No organizations found',
                            'Inga organisationer hittades'
                          )
                        : t(
                            'Type to search organizations',
                            'Skriv för att söka organisationer'
                          )}
                    </p>
                  ) : (
                    orgResults.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-black/[0.04]"
                        onClick={() => {
                          setSelectedOrg({
                            id: org.id,
                            name: org.name || t('Untitled', 'Namnlös'),
                          })
                          setOrgSearch('')
                        }}
                      >
                        {org.chosen_logo_url ? (
                          <img
                            src={org.chosen_logo_url}
                            alt=""
                            className="h-5 w-5 rounded object-contain"
                          />
                        ) : (
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="flex-1 truncate">
                          {org.name || t('Untitled', 'Namnlös')}
                        </span>
                      </button>
                    ))
                  )}
                </Column>
              </>
            )}
          </Column>
        )}

        <Textarea
          label={t('Email addresses', 'E-postadresser')}
          placeholder="name@company.com, teammate@company.com"
          rows={4}
          value={emails}
          onChange={(event) => setEmails(event.target.value)}
          description={emailHint}
        />

        <Row className="justify-end">
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!canSend}
            isLoading={isPending}
          >
            {t('Send invite', 'Skicka inbjudan')}
            <SendHorizonal className="ml-2 h-4 w-4" />
          </Button>
        </Row>
      </DialogContent>
    </Dialog>
  )
}
